import { forwardRef, InputHTMLAttributes } from 'react';

type DateInputType = 'date' | 'datetime-local' | 'time';

export interface DateFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  min?: string;
  max?: string;
  type?: DateInputType;
  className?: string;
  inputClassName?: string;
}

/**
 * DateField - Date input with label, error, and hint support
 */
const DateField = forwardRef<HTMLInputElement, DateFieldProps>(
  (
    {
      label,
      error,
      hint,
      required = false,
      id,
      min,
      max,
      type = 'date',
      className = '',
      inputClassName = '',
      ...props
    },
    ref
  ) => {
    const fieldId = id || props.name;

    const baseInputStyles =
      'w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-accent-500 bg-white dark:bg-dark-bg text-light-text dark:text-dark-text transition-colors';
    const errorStyles = error
      ? 'border-error focus:ring-error'
      : 'border-light-border dark:border-dark-border';

    return (
      <div className={`space-y-1.5 ${className}`}>
        {label && (
          <label
            htmlFor={fieldId}
            className="block text-sm font-medium text-light-text dark:text-dark-text"
          >
            {label}
            {required && <span className="text-error ml-0.5">*</span>}
          </label>
        )}
        <input
          ref={ref}
          id={fieldId}
          type={type}
          min={min}
          max={max}
          className={`${baseInputStyles} ${errorStyles} ${inputClassName}`}
          aria-invalid={!!error}
          aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          {...props}
        />
        {error && (
          <p id={`${fieldId}-error`} className="text-xs text-error animate-fade-in" role="alert">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${fieldId}-hint`} className="text-xs text-light-muted dark:text-dark-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

DateField.displayName = 'DateField';

export default DateField;
